import { db, storage } from '../config/firebase';
import { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

export interface VideoMeta {
  title: string;
  description: string;
  category: string;
  module: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  isIntro: boolean;
  requiredSubscription: boolean;
  moduleOrder: number; // Video number within the module
}

// Video progress document structure
export interface VideoProgress {
  userId: string;
  videoId: string;
  lastPosition: number; // seconds
  duration: number; // seconds
  completed: boolean;
  updatedAt: ReturnType<typeof serverTimestamp> | any; // serverTimestamp
  watchTime?: number; // total seconds watched
  viewCount?: number; // number of times video has been viewed
  hasStarted?: boolean; // whether user has ever opened this video
}

// ===== INITIALIZATION FUNCTIONS =====

// Initialize analytics document if it doesn't exist
export async function initializeAnalytics(): Promise<void> {
  try {
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (!analyticsDoc.exists()) {
      await setDoc(analyticsRef, {
        videosStarted: [],
        totalVideosStarted: 0,
        videosCompleted: [],
        totalVideosCompleted: 0,
        totalWatchtime: 0,
        lastWatchtimeUpdate: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
}

// Initialize user video progress document if it doesn't exist
export async function initializeUserVideoProgress(userId: string, videoId: string): Promise<VideoProgress> {
  try {
    const progressRef = doc(db, 'userVideoProgress', `${userId}_${videoId}`);
    const progressDoc = await getDoc(progressRef);
    
    if (!progressDoc.exists()) {
      const initialProgress: VideoProgress = {
        userId,
        videoId,
        lastPosition: 0,
        duration: 0,
        completed: false,
        updatedAt: serverTimestamp(),
        watchTime: 0,
        viewCount: 0,
        hasStarted: false
      };
      
      await setDoc(progressRef, initialProgress);
      return initialProgress;
    } else {
      const data = progressDoc.data() as VideoProgress;
      return data;
    }
  } catch (error) {
    console.error('Error initializing user video progress:', error);
    throw error;
  }
}

// Initialize all required Firebase structures
export async function initializeFirebaseStructures(): Promise<void> {
  try {
    // Initialize analytics
    await initializeAnalytics();
    
  } catch (error) {
    console.error('Error initializing Firebase structures:', error);
  }
}

// Upload a new video file and create a Firestore entry
export async function uploadVideo(
  file: File,
  meta: VideoMeta,
  onProgress?: (percent: number) => void
) {
  // 1. Upload video to Storage with progress
  const storageRef = ref(storage, `videos/${Date.now()}_${file.name}`);
  const uploadTask = uploadBytesResumable(storageRef, file);
  await new Promise<void>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress(Math.round(percent));
        }
      },
      (error) => reject(error),
      () => resolve()
    );
  });
  const videoUrl = await getDownloadURL(storageRef);

  // 2. Create Firestore doc - capitalize difficulty first letter
  const capitalizedDifficulty = meta.difficulty 
    ? meta.difficulty.charAt(0).toUpperCase() + meta.difficulty.slice(1).toLowerCase()
    : meta.difficulty;
  const videoDoc = await addDoc(collection(db, 'videos'), {
    ...meta,
    difficulty: capitalizedDifficulty,
    videoUrl,
    thumbnailUrl: '', // can be updated later
    uploadDate: new Date().toISOString(),
    isFree: !meta.requiredSubscription,
    requiredSubscription: meta.requiredSubscription,
    isIntro: meta.isIntro,
    moduleOrder: meta.moduleOrder, // Save the video number within the module
  });
  return videoDoc.id;
}

// Upload a thumbnail and attach to an existing video
export async function uploadThumbnail(file: File, videoId: string) {
  // 1. Upload thumbnail to Storage
  const storageRef = ref(storage, `thumbnails/${Date.now()}_${file.name}`);
  await uploadBytesResumable(storageRef, file);
  const thumbnailUrl = await getDownloadURL(storageRef);

  // 2. Update Firestore doc
  const videoDocRef = doc(db, 'videos', videoId);
  await updateDoc(videoDocRef, { thumbnailUrl });
  return thumbnailUrl;
}

// Upload a PDF and attach to an existing video (schede)
export async function uploadPdf(file: File, videoId: string) {
  // 1. Upload PDF to Storage
  const storageRef = ref(storage, `pdfs/${Date.now()}_${file.name}`);
  await uploadBytesResumable(storageRef, file);
  const pdfUrl = await getDownloadURL(storageRef);

  // 2. Update Firestore doc
  const videoDocRef = doc(db, 'videos', videoId);
  await updateDoc(videoDocRef, { schede: pdfUrl });
  return pdfUrl;
}

// Get videos missing thumbnails (for thumbnail upload UI)
export async function getVideosMissingThumbnails() {
  const q = query(collection(db, 'videos'), where('thumbnailUrl', '==', ''));
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ===== NEW CLEAN VIDEO TRACKING SYSTEM =====

// Get user's video progress (single document per user×video)
export async function getUserVideoProgress(userId: string, videoId: string): Promise<VideoProgress | null> {
  try {
    const progressRef = doc(db, 'userVideoProgress', `${userId}_${videoId}`);
    const snap = await getDoc(progressRef);
    
    if (snap.exists()) {
      const data = snap.data() as VideoProgress;
      return data;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error loading video progress:', error);
    return null;
  }
}

// Sync all users' total watchtime from their video progress (admin function)
export async function syncAllUsersTotalWatchtime(): Promise<void> {
  try {
    // Get all unique user IDs from video progress
    const progressRef = collection(db, 'userVideoProgress');
    const snap = await getDocs(progressRef);
    
    const userIds = new Set<string>();
    snap.forEach(doc => {
      const data = doc.data();
      if (data.userId) {
        userIds.add(data.userId);
      }
    });
    
    // Sync each user's total watchtime
    const syncPromises = Array.from(userIds).map(userId => syncUserTotalWatchtimeFromVideos(userId));
    await Promise.all(syncPromises);
    
  } catch (error) {
    console.error('Error syncing all users total watchtime:', error);
  }
}

// Update video progress (debounced, with merge) - SEPARATE from user total watchtime
export async function updateVideoProgress(
  userId: string,
  videoId: string,
  currentTime: number,
  duration: number,
  watchTimeIncrement?: number
): Promise<void> {
  try {
    const progressRef = doc(db, 'userVideoProgress', `${userId}_${videoId}`);
    const snap = await getDoc(progressRef);
    
    let existingData: Partial<VideoProgress> = {};
    if (snap.exists()) {
      existingData = snap.data() as VideoProgress;
    }
    
    const percent = currentTime / duration;
    const completed = percent >= 0.95;
    
    // Calculate new watchtime for THIS SPECIFIC VIDEO (add incrementally for video tracking)
    const currentWatchTime = existingData.watchTime || 0;
    const newWatchTime = watchTimeIncrement ? currentWatchTime + watchTimeIncrement : currentWatchTime;
    
    // Increment view count if this is the first time watching (percent > 0 and no previous viewCount)
    const viewCount = existingData.viewCount || 0;
    const newViewCount = (percent > 0 && viewCount === 0) ? 1 : viewCount;
    
    const updateData: Partial<VideoProgress> = {
      userId,
      videoId,
      lastPosition: completed ? duration : currentTime,
      duration,
      completed,
      updatedAt: serverTimestamp(),
      watchTime: newWatchTime,
      viewCount: newViewCount,
    };
    
    await setDoc(progressRef, updateData, { merge: true });
    
    // If video just completed, update analytics
    if (completed && !existingData.completed) {
      await updateVideoCompletedAnalytics(videoId, userId);
    }
    
  } catch (error) {
    console.error('Error updating video progress:', error);
  }
}

// Replace video progress data (for admin sync - replaces instead of adding)
export async function replaceVideoProgress(
  userId: string,
  videoId: string,
  currentTime: number,
  duration: number,
  totalWatchTime: number
): Promise<void> {
  try {
    const progressRef = doc(db, 'userVideoProgress', `${userId}_${videoId}`);
    const snap = await getDoc(progressRef);
    
    let existingData: Partial<VideoProgress> = {};
    if (snap.exists()) {
      existingData = snap.data() as VideoProgress;
    }
    
    const percent = currentTime / duration;
    const completionThreshold = 0.95; // Consistent with useVideoTracker
    const completed = percent >= completionThreshold;
    
    // Replace watchtime with total from cookies (for admin sync)
    const newWatchTime = totalWatchTime;
    
    // Keep existing view count or set to 1 if video has been watched
    const viewCount = existingData.viewCount || 0;
    const newViewCount = (percent > 0 && viewCount === 0) ? 1 : viewCount;
    
    // Set hasStarted to true if video has been watched (percent > 0)
    const hasStarted = percent > 0 || existingData.hasStarted || false;
    
    const updateData: Partial<VideoProgress> = {
      userId,
      videoId,
      lastPosition: completed ? duration : currentTime,
      duration,
      completed,
      updatedAt: serverTimestamp(),
      watchTime: newWatchTime,
      viewCount: newViewCount,
      hasStarted: hasStarted,
    };
    
    await setDoc(progressRef, updateData, { merge: true });
    
    // If video just completed, update analytics
    if (completed && !existingData.completed) {
      await updateVideoCompletedAnalytics(videoId, userId);
    }
    
  } catch (error) {
    console.error('Error replacing video progress:', error);
  }
}

// Update video started analytics
export async function updateVideoStartedAnalytics(videoId: string, userId: string): Promise<void> {
  try {
    // Ensure analytics document exists
    await initializeAnalytics();
    
    // Update global analytics
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (analyticsDoc.exists()) {
      const data = analyticsDoc.data();
      const videosStarted = data.videosStarted || [];
      
      // Only add if not already tracked
      if (!videosStarted.includes(`${userId}_${videoId}`)) {
        await updateDoc(analyticsRef, {
          videosStarted: [...videosStarted, `${userId}_${videoId}`],
          totalVideosStarted: videosStarted.length + 1
        });
        
        // Also sync the user's video stats
        await syncUserVideoStatsFromVideos(userId);
      }
    } else {
      // Create analytics document
      await setDoc(analyticsRef, {
        videosStarted: [`${userId}_${videoId}`],
        totalVideosStarted: 1,
        videosCompleted: [],
        totalVideosCompleted: 0,
        totalWatchtime: 0
      });
      
      // Also sync the user's video stats
      await syncUserVideoStatsFromVideos(userId);
    }
  } catch (error) {
    console.error('Error updating video started analytics:', error);
  }
}

// Update video completed analytics
export async function updateVideoCompletedAnalytics(videoId: string, userId: string): Promise<void> {
  try {
    // Ensure analytics document exists
    await initializeAnalytics();
    
    // Update global analytics
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (analyticsDoc.exists()) {
      const data = analyticsDoc.data();
      const videosCompleted = data.videosCompleted || [];
      
      // Only add if not already tracked
      if (!videosCompleted.includes(`${userId}_${videoId}`)) {
        await updateDoc(analyticsRef, {
          videosCompleted: [...videosCompleted, `${userId}_${videoId}`],
          totalVideosCompleted: videosCompleted.length + 1
        });
        
        // Also sync the user's video stats
        await syncUserVideoStatsFromVideos(userId);
      }
    } else {
      // Create analytics document
      await setDoc(analyticsRef, {
        videosStarted: [],
        totalVideosStarted: 0,
        videosCompleted: [`${userId}_${videoId}`],
        totalVideosCompleted: 1,
        totalWatchtime: 0
      });
      
      // Also sync the user's video stats
      await syncUserVideoStatsFromVideos(userId);
    }
  } catch (error) {
    console.error('Error updating video completed analytics:', error);
  }
}

// Update global watchtime analytics
export async function updateGlobalWatchtime(watchTimeIncrement: number): Promise<void> {
  try {
    // Ensure analytics document exists
    await initializeAnalytics();
    
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (analyticsDoc.exists()) {
      const data = analyticsDoc.data();
      const currentTotal = data.totalWatchtime || 0;
      const newTotal = currentTotal + watchTimeIncrement;
      
      await updateDoc(analyticsRef, {
        totalWatchtime: newTotal,
        lastWatchtimeUpdate: serverTimestamp()
      });
      
    } else {
      // Create analytics document
      await setDoc(analyticsRef, {
        videosStarted: [],
        totalVideosStarted: 0,
        videosCompleted: [],
        totalVideosCompleted: 0,
        totalWatchtime: watchTimeIncrement,
        lastWatchtimeUpdate: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating global watchtime:', error);
  }
}

// Update user's total watchtime in their profile
export async function updateUserTotalWatchtime(userId: string, watchTimeIncrement: number): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentTotal = userData.totalWatchtime || 0;
      const newTotal = currentTotal + watchTimeIncrement;
      
      await updateDoc(userRef, {
        totalWatchtime: newTotal,
        lastWatchtimeUpdate: serverTimestamp()
      });
      
    } else {
      // Create user document with watchtime
      await setDoc(userRef, {
        totalWatchtime: watchTimeIncrement,
        lastWatchtimeUpdate: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error updating user total watchtime:', error);
  }
}

// Get user's total watchtime from their profile
export async function getUserTotalWatchtime(userId: string): Promise<number> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const totalWatchtime = userData.totalWatchtime || 0;
      return totalWatchtime;
    } else {
      return 0;
    }
  } catch (error) {
    console.error('Error getting user total watchtime:', error);
    return 0;
  }
}

// Calculate user's total watchtime from all video progress documents
export async function calculateUserTotalWatchtimeFromVideos(userId: string): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const q = query(progressRef, where('userId', '==', userId));
    const snap = await getDocs(q);
    
    let totalWatchtime = 0;
    snap.forEach(doc => {
      const data = doc.data();
      totalWatchtime += data.watchTime || 0;
    });
    
    return totalWatchtime;
  } catch (error) {
    console.error('Error calculating user total watchtime from videos:', error);
    return 0;
  }
}

// Sync user's total watchtime from video progress (useful for data migration or verification)
export async function syncUserTotalWatchtimeFromVideos(userId: string): Promise<void> {
  try {
    const calculatedTotal = await calculateUserTotalWatchtimeFromVideos(userId);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentTotal = userData.totalWatchtime || 0;
      
      if (calculatedTotal !== currentTotal) {
        await updateDoc(userRef, {
          totalWatchtime: calculatedTotal,
          lastWatchtimeUpdate: serverTimestamp()
        });
      }
    } else {
      // Create user document with calculated watchtime
      await setDoc(userRef, {
        totalWatchtime: calculatedTotal,
        lastWatchtimeUpdate: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error syncing user total watchtime:', error);
  }
}

// Sync current user's cookie data to Firebase (for video player use)
export async function syncCurrentUserCookieToFirebase(userId: string): Promise<void> {
  try {
    if (!userId) {
      return;
    }

    // Get all cookies
    const allCookies = document.cookie.split(';');
    let syncedCount = 0;
    
    // Find all video progress cookies for this user
    for (const cookie of allCookies) {
      const trimmedCookie = cookie.trim();
      
      if (trimmedCookie.startsWith('videoProgress_')) {
        try {
          // Extract videoId from cookie name
          const cookieName = trimmedCookie.split('=')[0];
          const videoId = cookieName.replace('videoProgress_', '');
          
          // Parse cookie data - handle values that might contain equals signs
          const equalsIndex = trimmedCookie.indexOf('=');
          if (equalsIndex === -1) continue;
          
          const cookieValue = trimmedCookie.substring(equalsIndex + 1);
          if (!cookieValue) continue;
          
          const cookieData = JSON.parse(cookieValue);
          
          // Only sync if data is dirty (has been modified)
          if (cookieData.dirty) {
            
            // Use replaceVideoProgress to sync the data
            await replaceVideoProgress(
              userId,
              videoId,
              cookieData.lastPosition || 0,
              cookieData.duration || 0,
              cookieData.watchTime || 0
            );
            
            // Mark as clean after successful sync
            const cleanData = { ...cookieData, dirty: false };
            const cleanCookieValue = JSON.stringify(cleanData);
            document.cookie = `${cookieName}=${cleanCookieValue};expires=${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString()};path=/`;
            
            syncedCount++;
          }
        } catch (error) {
          console.error('Error syncing cookie data:', error);
        }
      }
    }
    
    if (syncedCount > 0) {
      // Update user's total watchtime and video stats
      await syncUserTotalWatchtimeFromVideos(userId);
      await syncUserVideoStatsFromVideos(userId);
    }
  } catch (error) {
    console.error('Error syncing current user cookie to Firebase:', error);
  }
}

// Get user's total watchtime for display (with fallback to calculation)
export async function getUserTotalWatchtimeForDisplay(userId: string): Promise<number> {
  try {
    // First try to get from user profile
    const profileWatchtime = await getUserTotalWatchtime(userId);
    
    if (profileWatchtime > 0) {
      return profileWatchtime;
    }
    
    // If profile has 0 or no data, calculate from video progress
    const calculatedWatchtime = await calculateUserTotalWatchtimeFromVideos(userId);
    
    // If calculated is different from profile, sync it
    if (calculatedWatchtime > 0 && calculatedWatchtime !== profileWatchtime) {
      await syncUserTotalWatchtimeFromVideos(userId);
    }
    
    return calculatedWatchtime;
  } catch (error) {
    console.error('Error getting user total watchtime for display:', error);
    return 0;
  }
}

// Note: Cookie syncing is now handled by individual users during video playback
// This function is kept for admin dashboard compatibility but does nothing
export async function syncAllUsersCookieData(): Promise<void> {
  // Cookie syncing is now handled by individual users during video playback
  // Admin dashboard should read from Firebase data that users sync themselves
}

// Get analytics data
export async function getAnalyticsData() {
  try {
    // Calculate total watchtime from all user video progress
    const totalWatchtime = await calculateGlobalTotalWatchtimeFromVideos();
    
    // Calculate total videos started from all user video progress
    const totalVideosStarted = await calculateGlobalTotalVideosStarted();
    
    // Calculate total videos completed from all user video progress
    const totalVideosCompleted = await calculateGlobalTotalVideosCompleted();
    
    return {
      totalWatchtime: totalWatchtime,
      totalVideosStarted: totalVideosStarted,
      totalVideosCompleted: totalVideosCompleted,
      source: 'Calculated from video progress'
    };
    
  } catch (error) {
    console.error('Error getting analytics data:', error);
    return {
      totalWatchtime: 0,
      totalVideosStarted: 0,
      totalVideosCompleted: 0,
      source: 'error'
    };
  }
}

// Calculate global total watchtime from all user video progress
export async function calculateGlobalTotalWatchtimeFromVideos(): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const snap = await getDocs(progressRef);
    
    let totalWatchtime = 0;
    snap.forEach(doc => {
      const data = doc.data();
      if (data.watchTime && data.watchTime > 0) {
        totalWatchtime += data.watchTime;
      }
    });
    
    return totalWatchtime;
  } catch (error) {
    console.error('Error calculating global total watchtime:', error);
    return 0;
  }
}

// Calculate global total videos started from all user video progress
export async function calculateGlobalTotalVideosStarted(): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const snap = await getDocs(progressRef);
    
    // Count unique user-video combinations that have been started
    const startedVideos = new Set<string>();
    
    snap.forEach(doc => {
      const data = doc.data();
      // Count videos that have been started (hasStarted = true OR viewCount > 0 for backward compatibility)
      if (data.hasStarted || (data.viewCount && data.viewCount > 0)) {
        startedVideos.add(`${data.userId}_${data.videoId}`);
      }
    });
    
    return startedVideos.size;
  } catch (error) {
    console.error('Error calculating global total videos started:', error);
    return 0;
  }
}

// Calculate global total videos completed from all user video progress
export async function calculateGlobalTotalVideosCompleted(): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const snap = await getDocs(progressRef);
    
    // Count unique user-video combinations that have been completed
    const completedVideos = new Set<string>();
    
    snap.forEach(doc => {
      const data = doc.data();
      // Count videos that have been completed
      if (data.completed) {
        completedVideos.add(`${data.userId}_${data.videoId}`);
      }
    });
    
    return completedVideos.size;
  } catch (error) {
    console.error('Error calculating global total videos completed:', error);
    return 0;
  }
}

// Get all video progress for a user (for admin dashboard)
export async function getUserAllVideoProgress(userId: string): Promise<VideoProgress[]> {
  try {
    const q = query(collection(db, 'userVideoProgress'), where('userId', '==', userId));
    const snap = await getDocs(q);
    return snap.docs.map(doc => doc.data() as VideoProgress);
  } catch (error) {
    console.error('Error getting user video progress:', error);
    return [];
  }
}

// Calculate user's total videos started from video progress documents
export async function calculateUserTotalVideosStarted(userId: string): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const q = query(progressRef, where('userId', '==', userId));
    const snap = await getDocs(q);
    
    let totalVideosStarted = 0;
    snap.forEach(doc => {
      const data = doc.data();
      // Count videos that have been started (hasStarted = true OR viewCount > 0 for backward compatibility)
      if (data.hasStarted || (data.viewCount && data.viewCount > 0)) {
        totalVideosStarted++;
      }
    });
    
    return totalVideosStarted;
  } catch (error) {
    console.error('Error calculating user total videos started:', error);
    return 0;
  }
}

// Calculate user's total videos completed from video progress documents
export async function calculateUserTotalVideosCompleted(userId: string): Promise<number> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const q = query(progressRef, where('userId', '==', userId));
    const snap = await getDocs(q);
    
    let totalVideosCompleted = 0;
    snap.forEach(doc => {
      const data = doc.data();
      // Count videos that have been completed
      if (data.completed) {
        totalVideosCompleted++;
      }
    });
    
    return totalVideosCompleted;
  } catch (error) {
    console.error('Error calculating user total videos completed:', error);
    return 0;
  }
}

// Sync user's total videos started and completed from video progress
export async function syncUserVideoStatsFromVideos(userId: string): Promise<void> {
  try {
    const calculatedVideosStarted = await calculateUserTotalVideosStarted(userId);
    const calculatedVideosCompleted = await calculateUserTotalVideosCompleted(userId);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentVideosStarted = userData.userTotalVideosStarted || 0;
      const currentVideosCompleted = userData.userTotalVideosCompleted || 0;
      
      const updates: any = {};
      let hasChanges = false;
      
      if (calculatedVideosStarted !== currentVideosStarted) {
        updates.userTotalVideosStarted = calculatedVideosStarted;
        hasChanges = true;
      }
      
      if (calculatedVideosCompleted !== currentVideosCompleted) {
        updates.userTotalVideosCompleted = calculatedVideosCompleted;
        hasChanges = true;
      }
      
      if (hasChanges) {
        await updateDoc(userRef, updates);
      }
    } else {
      // Create user document with calculated stats
      await setDoc(userRef, {
        userTotalVideosStarted: calculatedVideosStarted,
        userTotalVideosCompleted: calculatedVideosCompleted
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error syncing user video stats:', error);
  }
}

// Sync all users' video stats from their video progress (admin function)
export async function syncAllUsersVideoStats(): Promise<void> {
  try {
    // Get all users
    const usersSnap = await getDocs(collection(db, 'users'));
    const users = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Process each user
    for (const user of users) {
      if (user.id) {
        try {
          await syncUserVideoStatsFromVideos(user.id);
        } catch (error) {
          console.error(`Error syncing stats for user ${user.id}:`, error);
        }
      }
    }
    
  } catch (error) {
    console.error('Error syncing all users video stats:', error);
  }
}

// Get user's video stats for display (with fallback to calculation)
export async function getUserVideoStatsForDisplay(userId: string): Promise<{ started: number; completed: number }> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const profileStarted = userData.userTotalVideosStarted || 0;
      const profileCompleted = userData.userTotalVideosCompleted || 0;
      
      // If profile has data, return it
      if (profileStarted > 0 || profileCompleted > 0) {
        return { started: profileStarted, completed: profileCompleted };
      }
    }
    
    // If profile has no data, calculate from video progress
    const calculatedStarted = await calculateUserTotalVideosStarted(userId);
    const calculatedCompleted = await calculateUserTotalVideosCompleted(userId);
    
    // If calculated is different from profile, sync it
    if (calculatedStarted > 0 || calculatedCompleted > 0) {
      await syncUserVideoStatsFromVideos(userId);
    }
    
    return { started: calculatedStarted, completed: calculatedCompleted };
  } catch (error) {
    console.error('Error getting user video stats for display:', error);
    return { started: 0, completed: 0 };
  }
}

// ===== MIGRATION AND DATA FIXING FUNCTIONS =====

// Comprehensive migration function to fix existing data issues
export async function migrateAndFixVideoData(): Promise<void> {
  try {
    // Step 1: Fix video progress documents that might be missing viewCount
    await fixVideoProgressViewCounts();
    
    // Step 2: Sync all users' video stats from their video progress
    await syncAllUsersVideoStats();
    
    // Step 3: Sync all users' watchtime from their video progress
    await syncAllUsersTotalWatchtime();
    
    // Step 4: Verify and fix global analytics consistency
    await verifyAndFixGlobalAnalytics();
    
  } catch (error) {
    console.error('Error during video data migration:', error);
    throw error;
  }
}

// Fix video progress documents that might be missing viewCount
export async function fixVideoProgressViewCounts(): Promise<void> {
  try {
    const progressRef = collection(db, 'userVideoProgress');
    const snap = await getDocs(progressRef);
    
    let fixedCount = 0;
    const updatePromises: Promise<void>[] = [];
    
    snap.forEach(doc => {
      const data = doc.data();
      
      // If viewCount is missing or 0 but user has watched the video
      if ((!data.viewCount || data.viewCount === 0) && data.watchTime && data.watchTime > 0) {
        const updatePromise = updateDoc(doc.ref, {
          viewCount: 1,
          updatedAt: serverTimestamp()
        });
        updatePromises.push(updatePromise);
        fixedCount++;
      }
    });
    
    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);
    }
  } catch (error) {
    console.error('Error fixing video progress viewCounts:', error);
    throw error;
  }
}

// Verify and fix global analytics consistency
export async function verifyAndFixGlobalAnalytics(): Promise<void> {
  try {
    // Ensure analytics document exists
    await initializeAnalytics();
    
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    const analyticsDoc = await getDoc(analyticsRef);
    
    if (analyticsDoc.exists()) {
      const data = analyticsDoc.data();
      const videosStarted = data.videosStarted || [];
      const videosCompleted = data.videosCompleted || [];
      
      // Recalculate totals from the arrays to ensure consistency
      const actualTotalStarted = videosStarted.length;
      const actualTotalCompleted = videosCompleted.length;
      
      const updates: any = {};
      let needsUpdate = false;
      
      if (data.totalVideosStarted !== actualTotalStarted) {
        updates.totalVideosStarted = actualTotalStarted;
        needsUpdate = true;
      }
      
      if (data.totalVideosCompleted !== actualTotalCompleted) {
        updates.totalVideosCompleted = actualTotalCompleted;
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        await updateDoc(analyticsRef, updates);
      }
    }
  } catch (error) {
    console.error('Error verifying global analytics:', error);
    throw error;
  }
}

// Get migration status and recommendations
export async function getMigrationStatus(): Promise<{
  needsMigration: boolean;
  issues: string[];
  recommendations: string[];
}> {
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  try {
    // Check for users without totalWatchtime
    const usersSnap = await getDocs(collection(db, 'users'));
    const usersWithoutWatchtime = usersSnap.docs.filter(doc => {
      const data = doc.data();
      return !data.totalWatchtime && data.totalWatchtime !== 0;
    });
    
    if (usersWithoutWatchtime.length > 0) {
      issues.push(`${usersWithoutWatchtime.length} users missing totalWatchtime field`);
      recommendations.push('Run syncAllUsersTotalWatchtime() to fix');
    }
    
    // Check for users without video stats
    const usersWithoutStats = usersSnap.docs.filter(doc => {
      const data = doc.data();
      return !data.totalVideosStarted && data.totalVideosStarted !== 0;
    });
    
    if (usersWithoutStats.length > 0) {
      issues.push(`${usersWithoutStats.length} users missing video stats`);
      recommendations.push('Run syncAllUsersVideoStats() to fix');
    }
    
    return {
      needsMigration: issues.length > 0,
      issues,
      recommendations
    };
  } catch (error) {
    console.error('Error checking migration status:', error);
    return {
      needsMigration: true,
      issues: ['Error checking migration status'],
      recommendations: ['Check console for details']
    };
  }
}

// Clear all video progress cookies from the browser
export function clearAllVideoProgressCookies(): number {
  try {
    // Get all cookies
    const allCookies = document.cookie.split(';');
    let clearedCount = 0;
    
    // Find and clear all video progress cookies
    allCookies.forEach(cookie => {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith('videoProgress_')) {
        const cookieName = trimmedCookie.split('=')[0];
        
        // Clear the cookie by setting it to expire in the past
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        clearedCount++;
      }
    });
    
    return clearedCount;
  } catch (error) {
    console.error('Error clearing video progress cookies:', error);
    return 0;
  }
}

// Delete all watchtime and video progress data for all users (admin function)
export async function deleteAllWatchtimeAndVideoProgress(): Promise<{ deletedProgressCount: number; updatedUsersCount: number; updatedVideosCount: number; clearedCookiesCount: number }> {
  try {
    // 1. Clear all video progress cookies from browser
    const clearedCookiesCount = clearAllVideoProgressCookies();
    
    // 2. Delete all video progress documents
    const progressRef = collection(db, 'userVideoProgress');
    const progressSnap = await getDocs(progressRef);
    
    const deleteProgressPromises = progressSnap.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteProgressPromises);
    
    const deletedProgressCount = progressSnap.docs.length;
    
    // 3. Reset all users' watchtime and video stats to 0
    const usersRef = collection(db, 'users');
    const usersSnap = await getDocs(usersRef);
    
    const updateUserPromises = usersSnap.docs.map(doc => 
      updateDoc(doc.ref, {
        totalWatchtime: 0,
        userTotalVideosStarted: 0,
        userTotalVideosCompleted: 0,
        lastWatchtimeUpdate: serverTimestamp()
      })
    );
    await Promise.all(updateUserPromises);
    
    const updatedUsersCount = usersSnap.docs.length;
    
    // 4. Clear any analytics data stored directly on video documents
    const videosRef = collection(db, 'videos');
    const videosSnap = await getDocs(videosRef);
    
    const updateVideoPromises = videosSnap.docs.map(doc => {
      const data = doc.data();
      const updates: any = {};
      
      // Clear any potential analytics fields that might exist on video documents
      if (data.viewCount !== undefined) updates.viewCount = 0;
      if (data.favoriteCount !== undefined) updates.favoriteCount = 0;
      if (data.watchLaterCount !== undefined) updates.watchLaterCount = 0;
      if (data.watchTimeData !== undefined) updates.watchTimeData = {};
      if (data.popularSegments !== undefined) updates.popularSegments = {};
      if (data.totalWatchTime !== undefined) updates.totalWatchTime = 0;
      if (data.completedCount !== undefined) updates.completedCount = 0;
      if (data.startedCount !== undefined) updates.startedCount = 0;
      
      // Only update if there are fields to clear
      if (Object.keys(updates).length > 0) {
        return updateDoc(doc.ref, updates);
      }
      return Promise.resolve();
    });
    
    await Promise.all(updateVideoPromises);
    const updatedVideosCount = videosSnap.docs.length;
    
    // 5. Reset global analytics
    const analyticsRef = doc(db, 'analytics', 'videoStats');
    await setDoc(analyticsRef, {
      videosStarted: [],
      totalVideosStarted: 0,
      videosCompleted: [],
      totalVideosCompleted: 0,
      totalWatchtime: 0,
      lastWatchtimeUpdate: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    
    // 6. Check for and clear any other potential analytics collections
    const potentialAnalyticsCollections = [
      'videoAnalytics',
      'videoViews', 
      'videoFavorites',
      'videoWatchLater',
      'videoEngagement',
      'videoMetrics'
    ];
    
    for (const collectionName of potentialAnalyticsCollections) {
      try {
        const collectionRef = collection(db, collectionName);
        const snap = await getDocs(collectionRef);
        if (snap.docs.length > 0) {
          const deletePromises = snap.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deletePromises);
        }
      } catch (error) {
        // Collection doesn't exist, which is fine
      }
    }
    
    return { deletedProgressCount, updatedUsersCount, updatedVideosCount, clearedCookiesCount };
  } catch (error) {
    console.error('Error deleting all watchtime and video progress data:', error);
    throw error;
  }
}


